/* eslint-disable @typescript-eslint/no-explicit-any */
import Cookies from "universal-cookie";
import { RpcError, StatusCode } from "grpc-web";

import { ClaimsBFFControllerClient } from "../protos/Claims-bffServiceClientPb";
import { ProvidersServiceClient } from "../protos/ProvidersServiceClientPb";

import { RefreshToken, User } from "../../types";
import { EnvKeys } from "../../consts";
import { getEnvValue } from "../../helpers";
import { refreshTokenWithDedup } from "../../auth";

let authRedirectInProgress = false;

/**
 * Internal, static class to hold the Envoy URL
 * Mainly used so this property isn't publicly accessible
 */
class grpcBase {
	private static readonly _envoyUrl: string = process.env.REACT_APP_ENVOY_URL ?? "";
	private constructor() {}

	public static get getEnvoyUrl() {
		return this._envoyUrl;
	}

	// For sanity
	private set setEnvoyUrl(s: string) {}
}

/**
 * Handles setting metadata/headers for authentication
 */
export class AuthInterceptor {
	token: string;

	constructor(token: string) {
		this.token = token;
	}

	/**
	 * Handle redirect to login (called once to prevent multiple redirects)
	 */
	private handleRedirectToLogin() {
		if (authRedirectInProgress) return;
		authRedirectInProgress = true;

		const cookies = new Cookies(null, { path: "/" });
		const cookieKeys: Array<string> = Object.values(EnvKeys)
			.map((k: string | EnvKeys) => getEnvValue(k as EnvKeys))
			.filter((k: string) => k.length !== 0);

		cookieKeys.forEach((k) => cookies.remove(k));

		const currentPath = window.location.pathname + window.location.search;
		if (currentPath !== "/login") {
			localStorage.setItem("lastVisitedPage", currentPath);
		}

		localStorage.setItem("sessionExpired", "true");
		window.location.replace("/login");
	}

	private getRefreshContext(): { foundUser: User; foundRefreshToken: RefreshToken } {
		const cookies = new Cookies(null, { path: "/" });
		const foundUser: User = cookies.get(getEnvValue(EnvKeys.UserCookieKey));
		const foundRefreshToken: RefreshToken = cookies.get(getEnvValue(EnvKeys.RefreshCookieKey));

		if (!foundUser || !foundRefreshToken) {
			throw new Error("User or Refresh Token not found in cookies");
		}

		return { foundUser, foundRefreshToken };
	}

	private async refreshAuthToken(): Promise<string> {
		const { foundUser, foundRefreshToken } = this.getRefreshContext();
		return refreshTokenWithDedup(foundUser.id, foundRefreshToken.token);
	}

	private async handleUnaryAuthError(request: any, invoker: any, metadata: any) {
		if (authRedirectInProgress) {
			throw new Error("Authentication failed. Redirecting to login.");
		}

		try {
			const newToken = await this.refreshAuthToken();
			metadata.authorization = newToken;
			this.token = newToken;
			return invoker(request);
		} catch (refreshError) {
			console.error("Token refresh failed:", refreshError);
			this.handleRedirectToLogin();
			throw refreshError;
		}
	}

	private async handleStreamingAuthError(handler: any) {
		if (authRedirectInProgress) {
			handler(new Error("Authentication failed. Redirecting to login."));
			return;
		}

		try {
			const newToken = await this.refreshAuthToken();
			this.token = newToken;
			handler(new Error("Authentication token refreshed. Please retry the request."));
		} catch (refreshError) {
			console.error("Token refresh failed:", refreshError);
			this.handleRedirectToLogin();
			handler(refreshError);
		}
	}

	intercept(request: any, invoker: any) {
		const metadata = request.getMetadata();
		metadata.authorization = this.token;

		const result = invoker(request);

		// Check if it's a Promise (unary call)
		if (result && typeof result.then === "function") {
			// Handle unary calls with existing Promise-based logic
			return result.catch(async (e: RpcError) => {
				// Only UNAUTHENTICATED is an auth problem a refresh can fix.
				// PERMISSION_DENIED means the caller is authenticated and simply is
				// not allowed to perform this action -- refreshing cannot change
				// that, and because auth revokes the old refresh token on every
				// refresh, treating a denial as an auth failure tears down a
				// perfectly good session.
				if (e.code === StatusCode.UNAUTHENTICATED) {
					return this.handleUnaryAuthError(request, invoker, metadata);
				} else {
					// eslint-disable-next-line no-throw-literal
					throw e as RpcError;
				}
			});
		}

		// Check if it's a stream (streaming call)
		if (result && typeof result.on === "function") {
			// Handle streaming calls
			const originalErrorHandler = result.on.bind(result);
			let errorHandled = false;

			// Intercept error events to handle authentication failures
			result.on = (event: string, handler: any) => {
				if (event === "error" && !errorHandled) {
					const wrappedHandler = async (e: RpcError) => {
						// Same reasoning as the unary path: only UNAUTHENTICATED warrants
						// a refresh. A PERMISSION_DENIED is surfaced to the caller.
						if (e.code === StatusCode.UNAUTHENTICATED) {
							errorHandled = true;
							await this.handleStreamingAuthError(handler);
						} else {
							handler(e);
						}
					};
					return originalErrorHandler("error", wrappedHandler);
				} else {
					return originalErrorHandler(event, handler);
				}
			};

			return result;
		}

		// Fallback for other types of results
		return result;
	}
}

export const getBffRequest = (token?: string): ClaimsBFFControllerClient => {
	const clientOptions = token
		? {
				unaryInterceptors: [new AuthInterceptor(token)],
				streamInterceptors: [new AuthInterceptor(token)]
			}
		: undefined;
	return new ClaimsBFFControllerClient(grpcBase.getEnvoyUrl, null, clientOptions);
};

export const getProvidersRequest = (token?: string): ProvidersServiceClient => {
	const clientOptions = token
		? {
				unaryInterceptors: [new AuthInterceptor(token)],
				streamInterceptors: [new AuthInterceptor(token)]
			}
		: undefined;
	return new ProvidersServiceClient(grpcBase.getEnvoyUrl, null, clientOptions);
};
